/* eslint-disable react-refresh/only-export-components */
import { createBrowserRouter, Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import { PageShell } from "@/components/layout/PageShell";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { AppErrorPage } from "@/components/ui/AppErrorPage";

const LoginPage = lazy(() => import("@/pages/LoginPage"));
const ChatPage = lazy(() => import("@/pages/ChatPage"));
const UsersPage = lazy(() => import("@/pages/admin/UsersPage"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));
const CollectionsPage = lazy(
  () => import("@/features/collections/components/CollectionsPage")
);
const DocumentsPage = lazy(
  () => import("@/features/documents/components/DocumentsPage")
);
const JobsPage = lazy(() => import("@/features/jobs/components/JobsPage"));
const UploadPage = lazy(() => import("@/features/upload/components/UploadPage"));
const ApiKeysPage = lazy(() => import("@/pages/ApiKeysPage"));
const WorkspacesPage = lazy(() => import("@/pages/WorkspacesPage"));
const WorkspaceDetailPage = lazy(() => import("@/pages/WorkspaceDetailPage"));
const DownloadsPage = lazy(() => import("@/features/downloads/components/DownloadsPage"));
const ServicesPage = lazy(() => import("@/features/services/components/ServicesPage"));
const MetricsPage = lazy(() => import("@/pages/MetricsPage"));
const CrawlerDashboard = lazy(() => import("@/features/crawler/components/CrawlerDashboard"));
const CrawlerSourcePage = lazy(() => import("@/features/crawler/components/CrawlerSourcePage"));
const CrawlerFlowPage = lazy(() => import("@/features/crawler/components/CrawlerFlowPage"));
const CrawlerRunPage = lazy(() => import("@/features/crawler/components/CrawlerRunPage"));

const Fallback = () => <LoadingSpinner aria-label="Loading page" />;

export const router = createBrowserRouter([
  {
    path: "/login",
    element: (
      <Suspense fallback={<Fallback />}>
        <LoginPage />
      </Suspense>
    ),
  },
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <PageShell />,
        errorElement: <AppErrorPage />,
        children: [
          {
            index: true,
            element: <Navigate to="/chat" replace />,
          },
          {
            path: "/chat",
            element: (
              <Suspense fallback={<Fallback />}>
                <ChatPage />
              </Suspense>
            ),
          },
          {
            path: "/chat/:sessionId",
            element: (
              <Suspense fallback={<Fallback />}>
                <ChatPage />
              </Suspense>
            ),
          },
          {
            path: "/workspaces",
            element: (
              <Suspense fallback={<Fallback />}>
                <WorkspacesPage />
              </Suspense>
            ),
          },
          {
            path: "/workspaces/:id",
            element: (
              <Suspense fallback={<Fallback />}>
                <WorkspaceDetailPage />
              </Suspense>
            ),
          },
          {
            path: "/api-keys",
            element: (
              <Suspense fallback={<Fallback />}>
                <ApiKeysPage />
              </Suspense>
            ),
          },
          {
            element: <ProtectedRoute adminOnly />,
            children: [
              {
                path: "/collections",
                element: (
                  <Suspense fallback={<Fallback />}>
                    <CollectionsPage />
                  </Suspense>
                ),
              },
              {
                path: "/upload",
                element: (
                  <Suspense fallback={<Fallback />}>
                    <UploadPage />
                  </Suspense>
                ),
              },
              {
                path: "/downloads",
                element: (
                  <Suspense fallback={<Fallback />}>
                    <DownloadsPage />
                  </Suspense>
                ),
              },
              {
                path: "/jobs",
                element: (
                  <Suspense fallback={<Fallback />}>
                    <JobsPage />
                  </Suspense>
                ),
              },
              {
                path: "/documents",
                element: (
                  <Suspense fallback={<Fallback />}>
                    <DocumentsPage />
                  </Suspense>
                ),
              },
              {
                path: "/services",
                element: (
                  <Suspense fallback={<Fallback />}>
                    <ServicesPage />
                  </Suspense>
                ),
              },
              {
                path: "/metrics",
                element: (
                  <Suspense fallback={<Fallback />}>
                    <MetricsPage />
                  </Suspense>
                ),
              },
              {
                path: "/crawler",
                element: (
                  <Suspense fallback={<Fallback />}>
                    <CrawlerDashboard />
                  </Suspense>
                ),
              },
              {
                path: "/crawler/sources/:id",
                element: (
                  <Suspense fallback={<Fallback />}>
                    <CrawlerSourcePage />
                  </Suspense>
                ),
              },
              {
                path: "/crawler/flows/:id",
                element: (
                  <Suspense fallback={<Fallback />}>
                    <CrawlerFlowPage />
                  </Suspense>
                ),
              },
              {
                path: "/crawler/runs/:id",
                element: (
                  <Suspense fallback={<Fallback />}>
                    <CrawlerRunPage />
                  </Suspense>
                ),
              },
              {
                path: "/admin/users",
                element: (
                  <Suspense fallback={<Fallback />}>
                    <UsersPage />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: "*",
            element: (
              <Suspense fallback={<Fallback />}>
                <NotFoundPage />
              </Suspense>
            ),
          },
        ],
      },
    ],
  },
]);
